import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Droplets, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';
import QuoteForm from '../components/QuoteForm';

const WaterConnections = () => {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);

  // Full 73 areas: UK + England + Wales + 48 English counties + 22 Welsh areas
  const areaServed = [
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "AdministrativeArea", "name": "England" },
    { "@type": "AdministrativeArea", "name": "Wales" },
    // English counties (48)
    { "@type": "AdministrativeArea", "name": "Bedfordshire" },
    { "@type": "AdministrativeArea", "name": "Berkshire" },
    { "@type": "AdministrativeArea", "name": "Bristol" },
    { "@type": "AdministrativeArea", "name": "Buckinghamshire" },
    { "@type": "AdministrativeArea", "name": "Cambridgeshire" },
    { "@type": "AdministrativeArea", "name": "Cheshire" },
    { "@type": "AdministrativeArea", "name": "City of London" },
    { "@type": "AdministrativeArea", "name": "Cornwall" },
    { "@type": "AdministrativeArea", "name": "Cumbria" },
    { "@type": "AdministrativeArea", "name": "Derbyshire" },
    { "@type": "AdministrativeArea", "name": "Devon" },
    { "@type": "AdministrativeArea", "name": "Dorset" },
    { "@type": "AdministrativeArea", "name": "County Durham" },
    { "@type": "AdministrativeArea", "name": "East Riding of Yorkshire" },
    { "@type": "AdministrativeArea", "name": "East Sussex" },
    { "@type": "AdministrativeArea", "name": "Essex" },
    { "@type": "AdministrativeArea", "name": "Gloucestershire" },
    { "@type": "AdministrativeArea", "name": "Greater London" },
    { "@type": "AdministrativeArea", "name": "Greater Manchester" },
    { "@type": "AdministrativeArea", "name": "Hampshire" },
    { "@type": "AdministrativeArea", "name": "Herefordshire" },
    { "@type": "AdministrativeArea", "name": "Hertfordshire" },
    { "@type": "AdministrativeArea", "name": "Isle of Wight" },
    { "@type": "AdministrativeArea", "name": "Kent" },
    { "@type": "AdministrativeArea", "name": "Lancashire" },
    { "@type": "AdministrativeArea", "name": "Leicestershire" },
    { "@type": "AdministrativeArea", "name": "Lincolnshire" },
    { "@type": "AdministrativeArea", "name": "Merseyside" },
    { "@type": "AdministrativeArea", "name": "Norfolk" },
    { "@type": "AdministrativeArea", "name": "North Yorkshire" },
    { "@type": "AdministrativeArea", "name": "Northamptonshire" },
    { "@type": "AdministrativeArea", "name": "Northumberland" },
    { "@type": "AdministrativeArea", "name": "Nottinghamshire" },
    { "@type": "AdministrativeArea", "name": "Oxfordshire" },
    { "@type": "AdministrativeArea", "name": "Rutland" },
    { "@type": "AdministrativeArea", "name": "Shropshire" },
    { "@type": "AdministrativeArea", "name": "Somerset" },
    { "@type": "AdministrativeArea", "name": "South Yorkshire" },
    { "@type": "AdministrativeArea", "name": "Staffordshire" },
    { "@type": "AdministrativeArea", "name": "Suffolk" },
    { "@type": "AdministrativeArea", "name": "Surrey" },
    { "@type": "AdministrativeArea", "name": "Tyne and Wear" },
    { "@type": "AdministrativeArea", "name": "Warwickshire" },
    { "@type": "AdministrativeArea", "name": "West Midlands" },
    { "@type": "AdministrativeArea", "name": "West Sussex" },
    { "@type": "AdministrativeArea", "name": "West Yorkshire" },
    { "@type": "AdministrativeArea", "name": "Wiltshire" },
    { "@type": "AdministrativeArea", "name": "Worcestershire" },
    // Welsh areas (22)
    { "@type": "AdministrativeArea", "name": "Blaenau Gwent" },
    { "@type": "AdministrativeArea", "name": "Bridgend" },
    { "@type": "AdministrativeArea", "name": "Caerphilly" },
    { "@type": "AdministrativeArea", "name": "Cardiff" },
    { "@type": "AdministrativeArea", "name": "Carmarthenshire" },
    { "@type": "AdministrativeArea", "name": "Ceredigion" },
    { "@type": "AdministrativeArea", "name": "Conwy" },
    { "@type": "AdministrativeArea", "name": "Denbighshire" },
    { "@type": "AdministrativeArea", "name": "Flintshire" },
    { "@type": "AdministrativeArea", "name": "Gwynedd" },
    { "@type": "AdministrativeArea", "name": "Isle of Anglesey" },
    { "@type": "AdministrativeArea", "name": "Merthyr Tydfil" },
    { "@type": "AdministrativeArea", "name": "Monmouthshire" },
    { "@type": "AdministrativeArea", "name": "Neath Port Talbot" },
    { "@type": "AdministrativeArea", "name": "Newport" },
    { "@type": "AdministrativeArea", "name": "Pembrokeshire" },
    { "@type": "AdministrativeArea", "name": "Powys" },
    { "@type": "AdministrativeArea", "name": "Rhondda Cynon Taf" },
    { "@type": "AdministrativeArea", "name": "Swansea" },
    { "@type": "AdministrativeArea", "name": "Torfaen" },
    { "@type": "AdministrativeArea", "name": "Vale of Glamorgan" },
    { "@type": "AdministrativeArea", "name": "Wrexham" }
  ];

  // County names for visible "Where we work" section
  const counties = [
    "Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "City of London", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "County Durham", "East Riding of Yorkshire", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire",
    "Blaenau Gwent", "Bridgend", "Caerphilly", "Cardiff", "Carmarthenshire", "Ceredigion", "Conwy", "Denbighshire", "Flintshire", "Gwynedd", "Isle of Anglesey", "Merthyr Tydfil", "Monmouthshire", "Neath Port Talbot", "Newport", "Pembrokeshire", "Powys", "Rhondda Cynon Taf", "Swansea", "Torfaen", "Vale of Glamorgan", "Wrexham"
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://greenfrogutilities.co.uk/water-connections#service",
    "serviceType": "Water Connections",
    "name": "New Water Connections and Self Lay Water Mains",
    "description": "WIRS accredited self lay water connections across mainland UK. New water connections, new water mains, existing supply alterations, disconnections and relocations for developers and commercial sites.",
    "provider": { "@id": "https://greenfrogutilities.co.uk/#localbusiness" },
    "areaServed": areaServed,
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "certification",
      "name": "WIRS accredited self lay provider",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Water Industry Registration Scheme"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Water Connection Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "New Water Connections" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Self Lay Water Mains" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Water Supply Alterations" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Water Disconnections and Relocations" }}
      ]
    }
  };

  const faqData = [
    {
      question: "What is a WIRS accredited self lay provider?",
      answer: "A self lay provider, or SLP, is a contractor accredited under the Water Industry Registration Scheme to design and lay water mains that will be adopted into the public water network. Water companies only accept self lay work from a WIRS accredited provider. Green Frog Utilities is WIRS accredited."
    },
    {
      question: "How much does it cost to connect to mains water in the UK?",
      answer: "There is no standard figure. The cost of a new water connection depends on the distance from the existing main, the ground conditions, the size of supply needed and any traffic management required. A site survey and design are what turn that into a fixed quotation."
    },
    {
      question: "How do I apply for a new water connection for a new build?",
      answer: "You can apply through the incumbent water company, or you can appoint a WIRS accredited self lay provider to carry out the design and installation and manage the process with the water company on your behalf. The self lay route generally gives more control over the programme."
    },
    {
      question: "What is needed to get a quotation?",
      answer: "The site address, which utilities are required and, where known, the capacity or load. A design and quotation are then prepared, usually following a site survey."
    },
    {
      question: "Can water be installed at the same time as gas and electricity?",
      answer: "Yes. Where more than one utility is needed, a multi utility connection installs them together under one design and one programme, which avoids the site being opened up several times."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://greenfrogutilities.co.uk/services/connections" },
      { "@type": "ListItem", "position": 3, "name": "Water Connections", "item": "https://greenfrogutilities.co.uk/water-connections" }
    ]
  };

  const services = [
    "New water connections for developments, commercial units, industrial sites and build to rent schemes",
    "New water mains laid and prepared for adoption by the incumbent water company",
    "Existing supply alterations where a site's demand has changed",
    "Disconnections and relocations where a supply is in the way of construction or is no longer required",
    "Multi utility connections, where water is installed alongside gas and electricity in a single coordinated programme"
  ];

  const processSteps = [
    { step: "1", title: "Enquiry", desc: "The useful starting point is the site address, the utilities required and, where known, the capacity or load needed." },
    { step: "2", title: "Site survey and design", desc: "The connection is designed around the site layout, the existing network and the demand." },
    { step: "3", title: "Quotation", desc: "A fixed quotation is issued against that design." },
    { step: "4", title: "Approval", desc: "The design is agreed with the adopting water company." },
    { step: "5", title: "Installation", desc: "Excavation, mains laying and connection are carried out by our own teams." },
    { step: "6", title: "Connection and handover", desc: "The supply is commissioned and the paperwork for adoption is completed." }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div>
      <SEO 
        title="Water Connections | WIRS Accredited Self Lay | Green Frog Utilities"
        description="WIRS accredited self lay water connections across the UK. New water mains, existing supply alterations and relocations for developers and commercial sites."
        canonical="/water-connections"
        jsonLd={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <Droplets className="h-16 w-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              New Water Connections
            </h1>
            <p className="text-xl text-emerald-100 max-w-4xl mx-auto leading-relaxed">
              Green Frog Utilities designs and installs new water connections across mainland UK, from a single commercial supply to full water mains infrastructure on a new development. As a WIRS accredited self lay provider, we can carry out the work on the network ourselves rather than waiting in line behind the incumbent water company, which is usually what decides whether a connection lands on programme or holds the rest of the site up.
            </p>
          </div>
        </div>
      </section>

      {/* WIRS Accredited Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              WIRS accredited self lay provider
            </h2>
            <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
              <p>
                WIRS is the Water Industry Registration Scheme. It is the accreditation that allows a contractor to design and lay water mains that will become part of the public network, and water companies will only accept self lay work from an accredited provider.
              </p>
              <p>
                Green Frog Utilities holds WIRS accreditation, so your water connection can be delivered as a self lay project. In practice that means the design, the excavation, the mains laying and the connection are handled by one contractor working to your programme, with the adopting water company involved for approval and final connection rather than for every stage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            New connections, alterations and relocations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{service}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Who we work with
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Most water connection enquiries come from developers, main contractors, consultants and site managers rather than from the end occupier. That shapes how we work: a single point of contact, a design and quotation you can put in front of a client, and a programme date that means something.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How a new water connection works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((phase, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-emerald-300 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                <p className="text-emerald-100">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Water connections across the UK
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Green Frog Utilities works across mainland UK from its office in Tamworth, Staffordshire.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Links Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/services/connections">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Gas Connections
              </Button>
            </Link>
            <Link to="/electrical-connections">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Electrical Connections
              </Button>
            </Link>
            <Link to="/metering">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Metering
              </Button>
            </Link>
            <Link to="/accreditations">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Accreditations
              </Button>
            </Link>
            <Link to="/why-choose-green-frog">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Why Choose Us
              </Button>
            </Link>
            <Link to="/green-frog-group">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Utilities and Asset Management
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Where We Work Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Where we work</h2>
          <p className="text-gray-600 mb-6">
            Green Frog Utilities delivers gas, water and electrical connections across England and Wales, from a single accredited contractor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
            {counties.map((county, index) => (
              <span key={index} className="text-sm text-gray-500">{county}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Get a quotation for your water connection
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Tell us the site address and the utilities you need, and a design and quotation can be prepared.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#quote-form">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get a Quote
                </Button>
              </a>
              <a href="tel:+441675488644">
                <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                  Call 01675 488644
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <QuoteForm defaultConnectionType="water" defaultService="water-connections" title="Get a Water Connection Quote" />
    </div>
  );
};

export default WaterConnections;
